"use strict";

import "whatwg-fetch";
import { all } from "awaity/esm";
import { optionsGen } from "../helpers/api";
import { toTitleCase } from "../helpers/converters";
import { API_URL } from "../config";
import { toESQuery, toESGroupByQuery, cardinalityAggs, toESHistogramQuery, toESDateHistogramQuery, toESDateHistogramGroupByQuery, toESAvgsQuery } from "../helpers/query";
import { omit, difference } from "lodash";
import { SEARCH_IN_FIELDS, LARGE_FIELDS, getDistinctFamilyID, expandPublicationNumbersByFamilies } from "./api-landscape";

const AUTH = false;

export function getPatentDetail(ucid, withCases = false) {
  const qs = withCases ? "?with_cases=true" : "";
  return fetch(`${API_URL}/patents/${ucid}${qs}`, optionsGen("GET", false, AUTH)).then(resp => resp.json());
}

export function getPatentText(ucid, mode) {
  const qs = mode ? `?mode=${mode}` : "";
  return fetch(`${API_URL}/patents/${ucid}/full-language${qs}`, optionsGen("GET")).then(resp => resp.json())
}

export function getFullLanguage(ucid) {
  return getPatentText(ucid, "old").then(json => {
    return { claims: json.claims, description: json.description.split("\n") };
  });
}

export function getPearlFullLanguage(ucid) {
  return getPatentText(ucid, "pearl").then(json => {
    return { abstract: json.abstract, claims: json.claims, description: json.descriptionV2 };
  });
}

export function getFigures(ucid) {
  return fetch(`${API_URL}/patents/${ucid}/figures`, optionsGen("GET")).then(resp => resp.json());
}

export function getRenewalPrices(ucid) {
  return fetch(`${API_URL}/annuity/${ucid}`, optionsGen("GET", false, AUTH)).then(resp => resp.json());
}

export function getCPCTexts(cpcs) {
  return fetch(`${API_URL}/cpc/titles${cpcs.length > 0 ? `?=` + cpcs.map(c => `codes=${c}`).join("&") : ""}`, optionsGen("GET", false, AUTH)).then(resp => resp.json());
}

export function transformPublicationNumbers(publications) {
  return fetch(`${API_URL}/helpers/transform-publication-numbers`, optionsGen("POST", { publications }, true)).then(resp => resp.json());
}

// ES Search helpers

export const PATENTS_SEARCH_URL = `${API_URL}/patents/v6/_search`;

export function getPatentByGrantNumber(grantNumber) {
  const queryBody = { size: 1, query: { bool: { must: [], filter: [{ term: { grant_number: grantNumber } }, { term: { country: "US" } }] } } };
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", queryBody, false))
    .then(resp => resp.json())
    .then(json => json.hits.hits[0]);
}

function remapQuery(query) {
  if (query.full_text && !query.full_text_fields) {
    query.full_text_fields = SEARCH_IN_FIELDS;
  }
  return query;
}

export function esqPatents(query, highlight = false) {
  return toESQuery(remapQuery(query), LARGE_FIELDS, 20, highlight);
}

export function getPatents(query) {
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esqPatents(query, true), AUTH)).then(resp => resp.json());
}

export function getPatentsCount(queries) {
  const esq = toESQuery(remapQuery(queries), [], 0);
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => json.hits.total);
}

export function getDistinctValues(field, queries) {
  const esq = toESGroupByQuery(field, remapQuery(queries), 20);
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => json.aggregations[field].buckets.map(e => toTitleCase(e.key)));
}

export function getRatingHistogramData(field, queries) {
  const esq = toESGroupByQuery(field, omit(remapQuery(queries), ["rating_broadness", "rating_citation", "rating_litigation"]), 20);
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      const buckets = json.aggregations[field].buckets;
      let ratings = { D: 0, C: 0, B: 0, A: 0, AA: 0 };
      buckets.forEach(bucket => {
        const key = bucket.key.toUpperCase();
        ratings[key] = bucket.doc_count;
      });
      return ratings;
    });
}

export function getFamily(familyMembers) {
  const query = { publication_number: familyMembers, limit: familyMembers.length };
  return getPatents(query);
}

export function getCitations(ucids) {
  const query = { publication_number: ucids, limit: ucids.length };
  return getPatents(query);
}

export function getReferencedBy(ucid) {
  const query = { citations_pat: ucid, limit: 100 };
  return getPatents(query);
}

export function getFamilyByScore(queries, scoreField = "portfolio_score", interval = 1, familyField = "family_id") {
  const esq = cardinalityAggs(familyField, toESHistogramQuery(scoreField, interval, remapQuery(queries)), scoreField);
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json =>
      json.aggregations[scoreField].buckets.map(e => ({
        key: e.key,
        doc_count: e[familyField].value
      }))
    );
}

export function getDistinctFamilyIds(pubs, extended) {
  return getDistinctFamilyID(PATENTS_SEARCH_URL, null, pubs, extended);
}

export function expandPatentNumbersByFamilies(familyIDs, extended = false) {
  return expandPublicationNumbersByFamilies(PATENTS_SEARCH_URL, null, familyIDs, extended);
}

export function checkPublicationInclusion(esUrl, publications) {
  return fetch(
    esUrl,
    optionsGen(
      "POST",
      {
        query: {
          terms: { ucid_spif: publications }
        },
        size: 10000,
        _source: false
      },
      AUTH
    )
  )
    .then(resp => resp.json())
    .then(json => {
      const included = json.hits.hits.map(e => e._id);
      return { in: included, not_in: difference(publications, included) };
    });
}

export function checkFamilyIdInclusion(esUrl, familyIds) {
  return fetch(esUrl, optionsGen("POST", toESGroupByQuery("family_id", { family_id: familyIds }, familyIds.length), AUTH))
    .then(resp => resp.json())
    .then(json => {
      const included = json.aggregations["family_id"].buckets.map(e => e.key);
      return { in: included, not_in: difference(familyIds, included) };
    });
}

export function getPublicationByDate(queries, dateField, interval = "year") {
  const esq = toESDateHistogramQuery(dateField, interval, remapQuery(queries));
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => json.aggregations[dateField].buckets);
}

export function getPublicationByDateAndField(queries, dateField, field2, interval = "year", size = 20) {
  const esq = toESDateHistogramGroupByQuery(dateField, field2, interval, remapQuery(queries), size * 5);
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      let bbkeys = new Set([]);
      const res = json.aggregations[dateField].buckets.map(b => ({
        key: b.key,
        key_as_string: b.key_as_string,
        doc_count: b.doc_count, // this value may be different from sum of value[].doc_count because field2 may be aa multi-value field
        value: b[field2].buckets.map((bb, bbi) => {
          if (bbi < size / 2) bbkeys.add(bb.key);
          return {
            key: bb.key,
            doc_count: bb.doc_count
          };
        })
      }));
      return res.map(b => ({
        ...b,
        value: b.value.filter(bb => bbkeys.has(bb.key))
      }));
    });
}

export function countPatentsByField(queries, field, size = 20) {
  const esq = toESGroupByQuery(field, remapQuery(queries), size);
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => {
      let total = json.aggregations[field].sum_other_doc_count;
      json.aggregations[field].buckets.forEach(b => {
        total += b.doc_count;
      });
      return json.aggregations[field].buckets.map(b => ({
        ...b,
        doc_pct: b.doc_count / total
      }));
    });
}

export function getFieldAvg(queries, fields) {
  const esq = toESAvgsQuery(fields, remapQuery(queries));
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq, AUTH))
    .then(resp => resp.json())
    .then(json => fields.map(f => json.aggregations[f].value));
}

const getSum = distribution => {
  let total = 0;
  distribution.forEach(d => {
    total += d["key"] * d["family_id"]["value"];
  });
  return total;
};

const getAverage = distribution => {
  let total = 0;
  let docCount = 0;
  distribution.forEach(d => {
    total += d["key"] * d["family_id"]["value"];
    docCount += d["family_id"]["value"];
  });
  return total / docCount;
};

const getMedian = distribution => {
  let docCount = 0;
  distribution.forEach(d => {
    docCount += d["family_id"]["value"];
  });
  let cnt = 0;
  let median = 0;
  for (let i = 0; i < distribution.length; i++) {
    cnt += distribution[i]["family_id"]["value"];
    if (cnt > docCount / 2) {
      median = distribution[i];
      break;
    }
  }
  return median.key + 0.5 - (cnt - docCount / 2) / median["family_id"]["value"];
};

export function aggPatentsByPVIX(queries, field, size, sortBy) {
  const esQuery = toESQuery(remapQuery(queries), LARGE_FIELDS, 0);
  esQuery.aggs = {
    [field]: {
      terms: { field: field, size: size + 10 },
      aggs: {
        portfolio_score: {
          histogram: { field: "portfolio_score", interval: 1 },
          aggs: { family_id: { cardinality: { field: "family_id", precision_threshold: 10000 } } }
        }
      }
    }
  };
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esQuery, AUTH))
    .then(resp => resp.json())
    .then(json => {
      const metric = {
        pvix_sum: getSum,
        pvix_average: getAverage,
        pvix_median: getMedian
      }[sortBy];
      let results = [];
      json.aggregations[field].buckets.forEach(bucket => {
        results.push({ key: bucket.key, value: sortBy == "pvix_sum" ? metric(bucket.portfolio_score.buckets) : metric(bucket.portfolio_score.buckets).toFixed(2) });
      });
      results.sort((a, b) => {
        return b.value - a.value;
      });
      return results.slice(0, size);
    });
}

export function aggPatentsBySum(queries, groupByField, size, sumByField, order = "desc") {
  const esQuery = toESQuery(remapQuery(queries), LARGE_FIELDS, 0);
  esQuery.aggs = {
    [groupByField]: {
      terms: { field: groupByField, size: size, order: { [sumByField]: order } },
      aggs: {
        [sumByField]: { sum: { field: sumByField } }
      }
    }
  };
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esQuery, AUTH))
    .then(resp => resp.json())
    .then(json => {
      return json.aggregations[groupByField].buckets.map(bucket => ({ key: bucket.key, value: bucket[sumByField].value }));
    });
}

export function getLitigatedChallengedCnt(query) {
  const q1 = { ...query, country: "us", is_litigated: "true", last_litigated_at: "2012-09-16--" };
  const q2 = { ...query, country: "us", is_litigated: "true", is_challenged: "true" };
  const esq1 = toESQuery(remapQuery(q1), [], 0);
  const esq2 = toESQuery(remapQuery(q2), [], 0);
  return all([
    fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq1, AUTH)).then(resp => resp.json()),
    fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq2, AUTH)).then(resp => resp.json())
  ]).then(
    ([j1, j2]) => {
      const n1 = j1.hits.total.value;
      const n2 = j2.hits.total.value;
      return { "Not Challenged": n1 - n2, Challenged: n2 };
    }
  );
}

export function getChallengedLitigatedCnt(query) {
  const q1 = { ...query, country: "us", is_challenged: "true" };
  const q2 = { ...query, country: "us", is_litigated: "true", is_challenged: "true" };
  const esq1 = toESQuery(remapQuery(q1), [], 0);
  const esq2 = toESQuery(remapQuery(q2), [], 0);
  return all([
    fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq1, AUTH)).then(resp => resp.json()),
    fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq2, AUTH)).then(resp => resp.json())
  ]).then(
    ([j1, j2]) => {
      const n1 = j1.hits.total.value;
      const n2 = j2.hits.total.value;
      return { "Not Litigated": n1 - n2, Litigated: n2 };
    }
  );
}

export function getBreakdownByIndustry(query) {
  const q = { ...query, is_litigated: "true", is_challenged: "true" };
  const esq = toESGroupByQuery("industry", remapQuery(q));
  return fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq))
    .then(resp => resp.json())
    .then(json => {
      let res = {};
      const mapping = {
        t: "High-Tech",
        m: "Medical",
        o: "Other"
      };
      json.aggregations["industry"].buckets.forEach(e => (res[toTitleCase(mapping[e.key])] = e.doc_count));
      return res;
    });
}

export function getChallengedCnt(query) {
  const currentDate = new Date();
  const date = currentDate.getFullYear() + "-" + String(currentDate.getMonth() + 1).padStart(2, "0") + "-" + String(currentDate.getDate()).padStart(2, "0") + "--";
  const q1 = { ...query, country: "us", is_challenged: "true" };
  const q2 = { ...query, expiration_date: date, country: "us" };
  const esq1 = toESQuery(remapQuery(q1), [], 0);
  const esq2 = toESQuery(remapQuery(q2), [], 0);
  return all([
    fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq1, AUTH)).then(resp => resp.json()),
    fetch(PATENTS_SEARCH_URL, optionsGen("POST", esq2, AUTH)).then(resp => resp.json())
  ]).then(
    ([j1, j2]) => {
      const n1 = j1.hits.total.value;
      const n2 = j2.hits.total.value;
      return { "Not Challenged": n2 - n1, Challenged: n1 };
    }
  );
}

export function getReferenceNumbersForPatent(grantNum) {
  return fetch(`${API_URL}/patents/${grantNum}/refnum`, optionsGen("GET", false, true))
    .then(resp => resp.json())
}

export function getCitationForPatent(pubNum, payload) {
  return fetch(`${API_URL}/patents/${pubNum}/citation`, optionsGen("POST", payload, true))
    .then(resp => resp.json())
}